export type Store =
  | {
      getItem(k: string): string | null;
      setItem(k: string, v: string): void;
      removeItem(k: string): void;
      length?: number;
      key?(i: number): string | null;
    }
  | null
  | undefined;

const MAX_DRAFT = 6000;
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
const CHAT_KEY_PREFIXES = ["mb-chat-draft:", "mb-chat-seen:"];

function draftKey(userId: string, projectId: string): string {
  return `mb-chat-draft:${userId}:${projectId}`;
}

function seenKey(userId: string, projectId: string): string {
  return `mb-chat-seen:${userId}:${projectId}`;
}

export function saveDraft(
  store: Store,
  userId: string,
  projectId: string,
  text: string,
  now: number = Date.now()
): void {
  if (!store) return;
  try {
    const key = draftKey(userId, projectId);
    if (!text || text.trim().length === 0) {
      store.removeItem(key);
      return;
    }
    store.setItem(key, JSON.stringify({ t: now, text: text.slice(0, MAX_DRAFT) }));
  } catch {
    // ignore storage failures
  }
}

export function loadDraft(
  store: Store,
  userId: string,
  projectId: string,
  now: number = Date.now()
): string {
  if (!store) return "";
  try {
    const key = draftKey(userId, projectId);
    const value = store.getItem(key);
    if (typeof value !== "string") return "";
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      store.removeItem(key);
      return "";
    }
    const record = parsed as { t?: unknown; text?: unknown } | null;
    if (
      !record ||
      typeof record !== "object" ||
      typeof record.t !== "number" ||
      typeof record.text !== "string" ||
      now - record.t > MAX_DRAFT_AGE_MS ||
      record.t - now > MAX_CLOCK_SKEW_MS
    ) {
      store.removeItem(key);
      return "";
    }
    return record.text;
  } catch {
    return "";
  }
}

export function clearChatStorage(store: Store): void {
  if (!store || typeof store.length !== "number" || typeof store.key !== "function") return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (typeof key !== "string") continue;
      if (CHAT_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) keys.push(key);
    }
    for (const key of keys) store.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

export function saveSeen(
  store: Store,
  userId: string,
  projectId: string,
  messageId: string
): void {
  if (!store) return;
  try {
    store.setItem(seenKey(userId, projectId), messageId);
  } catch {
    // ignore storage failures
  }
}

export function loadSeen(
  store: Store,
  userId: string,
  projectId: string
): string {
  if (!store) return "";
  try {
    const value = store.getItem(seenKey(userId, projectId));
    return typeof value === "string" ? value : "";
  } catch {
    return "";
  }
}
